

































































































































































import { Component, Vue } from "vue-property-decorator";
import router from "@/router/router";
import { ClientDeviceAssignment, ClientDeviceRow } from "@/data/models/ClientAdministrationRow";
import { cachedClientDevicesForAdministration, getClientDevicesForAdministration } from "@/data/repository/ClientAdministrationRepository";
import { readThroughCache } from "@/utils/AdminReadCache";
import { setClientPageToolbarTitle } from "@/utils/ClientAdministrationTitle";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import AdminRefreshButtonComponent from "@/components/admin/AdminRefreshButton.vue";
import MenuIconWithMultiSelectionListComponent from "@/components/common/MultiSelectionFilterMenu.vue";
import {
  DEVICE_LIST_OPTIONS,
  DEVICE_LIST_SORTS,
  DeviceKindOption,
  DeviceListOption,
  DeviceListSort,
  deviceListOptionGroup,
  deviceListSelectionFromQuery,
  deviceListSelectionToQuery,
  deviceTypeLabelKey,
  orderedDeviceListSelection,
  PanelTypeOption,
  PeriodOption,
  selectDeviceListRows,
} from "@/utils/ClientDeviceList";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { clientDeviceLastContactText } from "@/utils/ClientDeviceLastContact";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";

/**
 * A client's devices in the admin portal, read-only: every device the client has or had, with the
 * type it is counted under, firmware, last contact and its whole assignment history.
 *
 * Opened from the client detail's Geräte card, either through "Alle Geräte" or from one of its
 * numbers, which arrives as a preset of ticked filter options in the query (`filters=PANELS,SOLD`)
 * and is applied once on entry; later filter changes stay out of the URL. Filters, sort and search
 * sit in the app bar and the header names the active filters, as in the Mandanten list. Filtering
 * uses the counts' own classification (`countedType`, `former`), so the list behind a number has
 * exactly that many rows.
 *
 * **Not the access boundary**: the data comes from `getClientDevicesForAdministration`, which repeats
 * the portal and `MANAGE_CLIENTS` checks server-side. Nothing here writes, and there are no links to
 * device pages, which the portal client cannot open. SIM data usage is not shown (noted for later).
 */
@Component({
  // Explicit, so Scaffold's keep-alive still matches it once the build minifies the class name.
  name: "ClientAdministrationDevicesComponent",
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "multi-selection-menu-icon": MenuIconWithMultiSelectionListComponent,
    "admin-refresh-button": AdminRefreshButtonComponent,
    "content-state": ContentStateComponent,
  },
})
export default class ClientAdministrationDevicesComponent extends Vue {
  devices: ClientDeviceRow[] = [];
  isLoading = true;
  /** Showing the cached devices while IRES Cloud is asked again (`AdminReadCache.ts`). */
  isRefreshing = false;
  loadError = false;
  searchQuery = "";
  /** The ticked filter options; none ticked shows every device. */
  selection: DeviceListOption[] = [];
  sort: DeviceListSort = DeviceListSort.SERIAL_NUMBER;
  /** null rather than undefined, so the template tracks it. */
  expandedSerialNumber: string | null = null;
  now = new Date();
  /** False while kept alive but not shown; gates the app bar portal. */
  componentActive = false;

  get clientId(): string {
    const value = this.$route.query.clientId;
    return typeof value === "string" ? value : "";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get hoverStyle(): string {
    const background = themed("", COLOR_LIST_ROW_HOVER_DARK);
    return background ? `background-color: ${background}` : "";
  }

  /** The column headings show only above rows. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  get rows(): ClientDeviceRow[] {
    return selectDeviceListRows(this.devices, this.selection, this.searchQuery, this.sort);
  }

  get filterOptions(): DeviceListOption[] {
    return DEVICE_LIST_OPTIONS.map((entry) => entry.option);
  }

  get sortOptions(): DeviceListSort[] {
    return DEVICE_LIST_SORTS;
  }

  /** The ticked options in menu order, as the header names them. */
  /** Below 600 px: the compact header (`AdminRefreshButton`, the ⋮ menu, the filters as a grey line). */
  get isPhone(): boolean {
    return this.$vuetify.breakpoint.xsOnly;
  }

  get filterDescription(): string {
    return orderedDeviceListSelection(this.selection).map((option) => this.optionName(option)).join(", ");
  }

  sortName(sort: DeviceListSort): string {
    return this.$t(`admin.clientDevices.sorts.${sort}`).toString();
  }

  optionKey(option: DeviceListOption): string {
    return option;
  }

  optionGroupName(option: DeviceListOption): string {
    return this.$t(`admin.clientDevices.filterGroups.${deviceListOptionGroup(option)}`).toString();
  }

  /** Panel types by the names IRES Office uses everywhere else; assignment types as on the card. */
  optionName(option: DeviceListOption): string {
    switch (option) {
      case PanelTypeOption.HYDRY_PANEL:
        return this.$t("iresDeviceType.panel").toString();
      case PanelTypeOption.HYDRY_SLIM:
        return this.$t("iresDeviceType.slim").toString();
      case PanelTypeOption.HYDRY_EDGE:
        return this.$t("iresDeviceType.edge").toString();
      case DeviceKindOption.PANELS:
      case DeviceKindOption.BOXES:
      case DeviceKindOption.MINIS:
        return this.$t(`admin.clientDevices.kinds.${option}`).toString();
      case PeriodOption.CURRENT:
      case PeriodOption.FORMER:
        return this.$t(`admin.clientDevices.scopes.${option}`).toString();
      default:
        return this.assignmentTypeLabel(option);
    }
  }

  deviceTypeLabel(device: ClientDeviceRow): string {
    return this.$t(deviceTypeLabelKey(device)).toString();
  }

  assignmentTypeLabel(type: string): string {
    const key = `admin.clients.detail.devices.assignmentTypes.${type}`;
    return this.$te(key) ? this.$t(key).toString() : type;
  }

  formatDate(millis: number | null): string {
    return millis === null ? EMPTY_VALUE : formatDateToDayMonthYear(new Date(millis)) ?? EMPTY_VALUE;
  }

  periodLabel(assignment: ClientDeviceAssignment): string {
    const end =
      assignment.endMillis === null
        ? this.$t("admin.clientDevices.open").toString()
        : this.formatDate(assignment.endMillis);

    return `${this.formatDate(assignment.startMillis)} – ${end}`;
  }

  /** The type the device is counted under and since when; "–" when it is counted under none. */
  assignmentLabel(device: ClientDeviceRow): string {
    if (!device.countedType || !device.currentAssignment) {
      return "–";
    }

    const since = this.$t("admin.clientDevices.since", [
      this.formatDate(device.currentAssignment.startMillis),
    ]).toString();

    return `${this.assignmentTypeLabel(device.countedType)} (${since})`;
  }

  firmwareLabel(device: ClientDeviceRow): string {
    const parts: string[] = [];

    if (device.kind === "panel") {
      if (device.firmware) {
        parts.push(device.firmware);
      }
      if (device.buildVersion !== null) {
        parts.push(this.$t("admin.clientDevices.build", [device.buildVersion]).toString());
      }
      if (device.configurationName) {
        parts.push(this.$t("admin.clientDevices.config", [device.configurationName]).toString());
      }
    } else {
      if (device.firmware) {
        parts.push(this.$t("admin.clientDevices.ipu", [device.firmware]).toString());
      }
      if (device.displayFirmware) {
        parts.push(this.$t("admin.clientDevices.display", [device.displayFirmware]).toString());
      }
    }

    return parts.length > 0 ? parts.join(" · ") : "–";
  }

  /** "IoT vor 2 h", through the same formatter as the HyDry device list. */
  lastContactLabel(device: ClientDeviceRow): string {
    return clientDeviceLastContactText(this, this.now, device);
  }

  toggle(serialNumber: string) {
    this.expandedSerialNumber = this.expandedSerialNumber === serialNumber ? null : serialNumber;
  }

  onBack() {
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: this.clientId } });
  }

  onSensorDataExport() {
    router.push({
      name: ComponentName.CLIENT_ADMINISTRATION_SENSOR_DATA_EXPORT,
      query: { clientId: this.clientId, ...deviceListSelectionToQuery(this.selection) },
    });
  }

  /** The cached devices at once when this client's were read before in this tab, then IRES Cloud's. */
  async load() {
    const cached = this.clientId === "" ? null : cachedClientDevicesForAdministration(this.clientId);
    this.isLoading = cached === null;
    this.isRefreshing = cached !== null;
    this.loadError = false;

    try {
      await readThroughCache(
        cached,
        () => (this.clientId === "" ? Promise.resolve([]) : getClientDevicesForAdministration(this.clientId)),
        (devices) => {
          this.devices = devices;
          this.now = new Date();
        }
      );
    } catch {
      // Swallowed on purpose: the screen shows its own failure state (a refusal from Cloud
      // included), and the refresh button is the only useful action.
      this.loadError = true;
      this.devices = [];
    } finally {
      this.isLoading = false;
      this.isRefreshing = false;
    }
  }

  /** As {@link load}, without the loading state, for the list shown again; a failure keeps the rows. */
  async reloadInBackground() {
    if (this.clientId === "") {
      return;
    }
    try {
      this.devices = await getClientDevicesForAdministration(this.clientId);
      this.now = new Date();
      this.loadError = false;
    } catch {
      // Swallowed on purpose: the list keeps what it showed, and the refresh button reloads on demand.
    }
  }

  mounted() {
    this.componentActive = true;
    setClientPageToolbarTitle(this.clientId, this.$t("admin.clientDevices.title").toString());
    // A preset from the Geräte card, applied once; later changes stay out of the URL.
    this.selection = deviceListSelectionFromQuery(this.$route.query as Record<string, unknown>);
    this.load();
  }

  /**
   * Back from the cache (the Sensordaten-Export only reads): the list is shown as it was, kept per
   * client URL; only the app bar and the "last contact" ages need refreshing.
   */
  /** Shown again: the old rows at once, then read again quietly; skipped while a read runs (the first visit). */
  activated() {
    this.componentActive = true;
    setClientPageToolbarTitle(this.clientId, this.$t("admin.clientDevices.title").toString());
    this.now = new Date();
    if (!this.isLoading && !this.isRefreshing) {
      this.reloadInBackground();
    }
  }

  deactivated() {
    this.componentActive = false;
  }
}
